import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  FeatureFlagAdminResponse,
  FeatureFlagTableRow,
  FeatureFlagTableSortDirection,
  FeatureFlagsClientFilter,
} from '../../../types/features/featureFlags';
import { mapTargetClientToApiClientApplication } from '../mappers/featureFlagPayloadMapper';
import { mapFeatureFlagResponseToTableRow } from '../mappers/featureFlagTableRowMapper';
import featureFlagAdminService from '../services/featureFlagAdminService';

const getListErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Failed to load feature flags.';
};

export const useFeatureFlagsList = (
  clientFilter: FeatureFlagsClientFilter,
  search: string,
  tableSortDirection: FeatureFlagTableSortDirection,
) => {
  const requestSequenceRef = useRef(0);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagAdminResponse[]>([]);
  const [featureRowsError, setFeatureRowsError] = useState('');
  const [featureRowsLoading, setFeatureRowsLoading] = useState(false);

  const handleRefreshFeatureRows = useCallback(async () => {
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    setFeatureRowsError('');
    setFeatureRowsLoading(true);

    try {
      const response = await featureFlagAdminService.getFeatureFlags(
        mapTargetClientToApiClientApplication(clientFilter),
      );

      if (requestSequenceRef.current === requestSequence) {
        setFeatureFlags(response);
      }
    } catch (error) {
      if (requestSequenceRef.current === requestSequence) {
        setFeatureFlags([]);
        setFeatureRowsError(getListErrorMessage(error));
      }
    } finally {
      if (requestSequenceRef.current === requestSequence) {
        setFeatureRowsLoading(false);
      }
    }
  }, [clientFilter]);

  useEffect(() => {
    void handleRefreshFeatureRows();
  }, [handleRefreshFeatureRows]);

  const featureRows = useMemo<FeatureFlagTableRow[]>(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filteredRows = featureFlags
      .map(mapFeatureFlagResponseToTableRow)
      .filter((row) => {
        if (!normalizedSearch) {
          return true;
        }

        return [
          row.displayName,
          row.featureKey,
        ].some((value) => value.toLowerCase().includes(normalizedSearch));
    });

    return [...filteredRows].sort((leftRow, rightRow) => {
      const difference = leftRow.assignmentCount - rightRow.assignmentCount;
      return tableSortDirection === 'asc' ? difference : -difference;
    });
  }, [featureFlags, search, tableSortDirection]);

  return {
    featureRows,
    featureRowsError,
    featureRowsLoading,
    handleRefreshFeatureRows,
  };
};
